import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { projectActivitiesApi } from '../../../api/projectWorkspace';
import { ApiError } from '../../../api/client';
import { useToast } from '../../../components/Toast';
import { CommentThread } from '../../../components/CommentThread';
import { usePermission } from '../../../auth/AuthContext';
import { Project, ProjectActivity } from '../../../types/projects';

/** Builds an inclusive list of {year, month, key:'YYYY-MM', label} between two dates. */
function buildMonthRange(startDate: string, endDate: string) {
  const months: { key: string; label: string }[] = [];
  const start = new Date(startDate + 'T00:00:00');
  const end = new Date(endDate + 'T00:00:00');
  const cursor = new Date(start.getFullYear(), start.getMonth(), 1);
  const last = new Date(end.getFullYear(), end.getMonth(), 1);
  while (cursor <= last) {
    const year = cursor.getFullYear();
    const month = cursor.getMonth() + 1;
    const key = `${year}-${String(month).padStart(2, '0')}`;
    const label = cursor.toLocaleDateString(undefined, { month: 'short', year: '2-digit' });
    months.push({ key, label });
    cursor.setMonth(cursor.getMonth() + 1);
  }
  return months;
}

function parsePlannedMonths(value: ProjectActivity['planned_months']): string[] {
  if (!value) return [];
  if (Array.isArray(value)) return value;
  try {
    const parsed = JSON.parse(value as unknown as string);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

/**
 * Activities are created from the Logical Framework tab (attached to
 * their Output). This tab is purely the Gantt view over that same
 * activity list: one row per activity, one column per project month,
 * tick a cell to mark that activity as planned for that month.
 */
export function ProjectWorkplanTab({ project }: { project: Project }) {
  const canManage = usePermission('project_workplan.manage');
  const { showError } = useToast();
  const queryClient = useQueryClient();

  const activitiesQuery = useQuery({ queryKey: ['project-activities', project.id], queryFn: () => projectActivitiesApi.listForProject(project.id) });
  const months = useMemo(() => buildMonthRange(project.start_date, project.end_date), [project.start_date, project.end_date]);

  const [pendingId, setPendingId] = useState<number | null>(null);
  const toggleMonthMutation = useMutation({
    mutationFn: (activity: ProjectActivity) =>
      projectActivitiesApi.update(activity.id, {
        activityCode: activity.activity_code,
        name: activity.name,
        responsibleUserId: activity.responsible_user_id,
        locationId: activity.location_id,
        plannedStartDate: activity.planned_start_date,
        plannedEndDate: activity.planned_end_date,
        plannedMonths: activity.planned_months,
        status: activity.status,
        progressPercent: activity.progress_percent,
        notes: activity.notes,
      }),
    onMutate: (activity) => setPendingId(activity.id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['project-activities', project.id] }),
    onError: (err) => showError(err instanceof ApiError ? err.message : 'Failed to update month.'),
    onSettled: () => setPendingId(null),
  });

  function toggleCell(activity: ProjectActivity, monthKey: string) {
    if (!canManage) return;
    const current = parsePlannedMonths(activity.planned_months);
    const next = current.includes(monthKey) ? current.filter((m) => m !== monthKey) : [...current, monthKey].sort();
    toggleMonthMutation.mutate({ ...activity, planned_months: next });
  }

  if (activitiesQuery.isLoading) return <p className="muted">Loading…</p>;

  const activities = activitiesQuery.data?.data ?? [];

  return (
    <>
      <div className="page-header"><h2>Work Plan</h2></div>
      <CommentThread projectId={project.id} entityType="workplan" />
      <p className="muted">Activities are added from the Logical Framework tab, under each Output. Tick the months each activity is planned to run.</p>

      {activities.length === 0 && (
        <div className="card"><p className="muted">No activities yet. Add activities under an Output in the Logical Framework tab first.</p></div>
      )}

      {activities.length > 0 && (
        <div className="card gantt-card">
          <div className="gantt-scroll">
            <table className="gantt-table">
              <thead>
                <tr>
                  <th className="gantt-activity-col">Activity</th>
                  {months.map((m) => <th key={m.key}>{m.label}</th>)}
                </tr>
              </thead>
              <tbody>
                {activities.map((activity) => {
                  const planned = parsePlannedMonths(activity.planned_months);
                  return (
                    <tr key={activity.id}>
                      <td className="gantt-activity-col">
                        <span className="code-badge code-badge-activity">{activity.activity_code}</span> {activity.name}
                      </td>
                      {months.map((m) => {
                        const isTicked = planned.includes(m.key);
                        const isSaving = pendingId === activity.id && toggleMonthMutation.isPending;
                        return (
                          <td key={m.key} className="gantt-cell">
                            <button
                              type="button"
                              className={`gantt-tick ${isTicked ? 'gantt-tick-on' : ''}`}
                              disabled={!canManage || isSaving}
                              onClick={() => toggleCell(activity, m.key)}
                              aria-label={`${activity.name} - ${m.label}`}
                            >
                              {isTicked ? '✓' : ''}
                            </button>
                          </td>
                        );
                      })}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </>
  );
}
